<template>
  <div
    class="my-2 flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs"
    :class="statusClasses"
  >
    <!-- Status icon -->
    <div class="flex-shrink-0">
      <div
        v-if="status === 'running'"
        class="h-3.5 w-3.5 animate-spin rounded-full border-[1.5px] border-indigo-200 border-t-indigo-600 dark:border-indigo-700 dark:border-t-indigo-400"
      />
      <CheckCircleIcon
        v-else-if="status === 'completed'"
        class="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400"
      />
      <ExclamationCircleIcon
        v-else-if="status === 'error'"
        class="h-3.5 w-3.5 text-red-500 dark:text-red-400"
      />
    </div>

    <!-- Tool label -->
    <span class="font-medium">{{ displayLabel }}</span>
  </div>
</template>

<script setup lang="ts">
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/vue/16/solid'

const props = defineProps<{
  toolName: string
  status: 'running' | 'completed' | 'error'
  toolArguments?: Record<string, any>
  toolError?: string
}>()

/** Map tool names to friendly, concise labels. */
const toolLabels: Record<string, string> = {
  // Read tools
  get_flow_details: 'Read flow structure',
  get_flow_analytics: 'Loaded flow metrics',
  list_flows: 'Found flows',
  get_question_details: 'Read question details',
  list_questions: 'Found questions',
  list_unused_questions: 'Found unused questions',
  get_dimension_details: 'Loaded segment group',
  get_segment_distribution: 'Loaded audience data',
  list_dimensions: 'Found segment groups',
  get_campaign_details: 'Read campaign',
  list_campaigns: 'Found campaigns',
  get_campaign_performance: 'Loaded campaign metrics',
  get_widget_details: 'Read widget config',
  list_widgets: 'Found widgets',
  analyze_website_for_widget_design: 'Analyzed website',
  get_offer_details: 'Read offer',
  get_offer_performance: 'Loaded offer metrics',
  list_offers: 'Found offers',
  list_templates: 'Browsed templates',
  get_template: 'Loaded template',
  get_template_structure: 'Analyzed template',
  get_survey_responses: 'Read survey data',
  get_consulting_advice: 'Researched best practices',
  build_condition: 'Built condition',
  check_question_sync_status: 'Checked sync status',
  get_esp_custom_fields: 'Loaded custom fields',
  get_esp_feature_support: 'Checked platform features',
  get_dimension_sync_mapping: 'Loaded sync mappings',
  get_available_tags: 'Found tags',
  get_available_scores: 'Found scores',
  get_set_data_capabilities: 'Checked data capabilities',
  get_dashboard_metrics: 'Loaded dashboard',
  get_traffic_overview: 'Analyzed traffic',
  get_most_engaged_visitors: 'Found top visitors',
  get_conversion_ready_visitors: 'Found ready-to-convert visitors',
  get_visitor_esp_data: 'Loaded visitor profile',
  get_website_pages: 'Found website pages',
  get_visitors_by_page_visits: 'Found visitors by page',
  get_email_personalization_context: 'Loaded email setup',
  get_existing_emails: 'Read existing emails',
  get_current_email: 'Loaded email content',
  get_page_context: 'Loaded page content',
  list_pages: 'Found pages',
  list_available_flows: 'Found embeddable flows',
  fetch_website_branding: 'Extracted branding',
  fetch_website_screenshot: 'Captured screenshot',
  build_flow_modification: 'Generated flow changes',
  get_plan_reference: 'Loaded plan reference',
  get_documentation: 'Looked up docs',
  get_flow_node_documentation: 'Loaded node reference',
  recall_past_conversations: 'Recalled past conversations',
  get_my_recent_actions: 'Reviewed recent actions',
  propose_plan: 'Created action plan',
  search_help_articles: 'Searched help docs',
  get_step_type_documentation: 'Loaded step type docs',
  get_flow_best_practices: 'Reviewed best practices',
  get_condition_sources: 'Loaded condition sources',
  validate_condition: 'Validated condition',

  // MCP Bridge CRUD tools
  create_dimension: 'Created segment group',
  get_dimension: 'Read segment group',
  list_dimensions_full: 'Listed all segment groups',
  update_dimension: 'Updated segment group',
  delete_dimension: 'Deleted segment group',
  create_segment: 'Created segment',
  update_segment: 'Updated segment',
  delete_segment: 'Deleted segment',
  create_question: 'Created question',
  get_question: 'Read question',
  update_question: 'Updated question',
  delete_question_resource: 'Deleted question',
  create_question_option: 'Created option',
  update_question_option: 'Updated option',
  delete_question_option: 'Deleted option',
  create_flow: 'Created flow',
  get_flow: 'Read flow',
  update_flow: 'Updated flow',
  delete_flow: 'Deleted flow',
  create_flow_node: 'Added flow node',
  update_flow_node: 'Updated flow node',
  delete_flow_node: 'Removed flow node',
  move_flow_node: 'Moved flow node',
  create_widget: 'Created widget',
  get_widget: 'Read widget',
  update_widget_resource: 'Updated widget',
  delete_widget_resource: 'Deleted widget',
  create_offer: 'Created offer',
  get_offer: 'Read offer',
  update_offer: 'Updated offer',
  delete_offer_resource: 'Deleted offer',
  create_goal: 'Created goal',
  get_goal: 'Read goal',
  update_goal: 'Updated goal',
  delete_goal: 'Deleted goal',
  create_phrase: 'Created phrase',
  get_phrase: 'Read phrase',
  update_phrase: 'Updated phrase',
  delete_phrase: 'Deleted phrase',
  create_campaign: 'Created campaign',
  get_campaign: 'Read campaign',
  update_campaign: 'Updated campaign',
  delete_campaign: 'Deleted campaign',
  undo_last_change: 'Undid last change'
}

const displayLabel = computed(() => {
  // Use the friendly label if available
  const label = toolLabels[props.toolName]
  if (label) {
    // For running state, use present tense
    if (props.status === 'running') {
      return label
        .replace(/^(Created|Updated|Deleted|Added|Removed|Moved|Read|Found|Loaded|Built|Analyzed|Extracted|Captured|Generated|Searched|Reviewed|Recalled|Checked|Browsed|Researched|Listed|Undid|Looked up|Validated)/, (match) => {
          const map: Record<string, string> = {
            Created: 'Creating',
            Updated: 'Updating',
            Deleted: 'Deleting',
            Added: 'Adding',
            Removed: 'Removing',
            Moved: 'Moving',
            Read: 'Reading',
            Found: 'Finding',
            Loaded: 'Loading',
            Built: 'Building',
            Analyzed: 'Analyzing',
            Extracted: 'Extracting',
            Captured: 'Capturing',
            Generated: 'Generating',
            Searched: 'Searching',
            Reviewed: 'Reviewing',
            Recalled: 'Recalling',
            Checked: 'Checking',
            Browsed: 'Browsing',
            Researched: 'Researching',
            Listed: 'Listing',
            Undid: 'Undoing',
            'Looked up': 'Looking up',
            Validated: 'Validating'
          }
          return map[match] || match
        })
    }
    return label
  }

  // Fallback: humanize the tool name
  return props.toolName.replace(/_/g, ' ').replace(/^(get|list|create|update|delete) /, '')
})

const statusClasses = computed(() => {
  switch (props.status) {
    case 'running':
      return 'border-indigo-200 bg-indigo-50/50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300'
    case 'completed':
      return 'border-slate-200 bg-slate-50/50 text-slate-500 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-400'
    case 'error':
      return 'border-red-200 bg-red-50/50 text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
  }
})
</script>
